import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getCurrentUser, logoutUser } from "../../APIs/authApi";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Read token instantly from localStorage — no API call needed for PrivateRoute checks
  const [token, setTokenState] = useState(() => localStorage.getItem("access_token"));
  const [user, setUser] = useState(null);
  // loading = true only while we hydrate `user` from a stored token after a page refresh
  const [loading, setLoading] = useState(!!localStorage.getItem("access_token"));

  // On mount: if a token is present, fetch the current user to hydrate `user` state.
  // If the token is invalid the interceptor will 401, we clear state here too.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    getCurrentUser()
      .then((res) => setUser(res.data ?? res))
      .catch(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        setTokenState(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Call after any successful login (normal or social).
  // authApi.loginUser already writes tokens to localStorage; this syncs React state.
  // If userData is null but accessToken is provided, fetches current user automatically.
  const login = useCallback(async (userData, accessToken) => {
    setTokenState(accessToken ?? null);
    if (userData) {
      setUser(userData);
    } else if (accessToken) {
      try {
        const res = await getCurrentUser();
        setUser(res.data ?? res);
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, []);

  // Call on logout — blacklists token server-side and clears everything.
  const logout = useCallback(async () => {
    await logoutUser();
    setUser(null);
    setTokenState(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
